import apiClient from './apiClient';
// Example: Updated userApi.ts for optimized user routes

export const userApi = {
  /**
   * Get all users - now with optimized query including firm, city, country data
   */
  getAllUsers: async () => {
    try {
      console.log('Fetching users from optimized API...');
      const response = await apiClient.get('/api/v2/web/users');
      console.log('API response data:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error in getAllUsers:', error);
      throw error;
    }
  },
  
  /**
   * Get a single user by ID - now with complete data in one call
   */
  getUserById: async (userId: string) => {
    try {
      const response = await apiClient.get(`/api/v2/web/users/${userId}`);
      return response.data;
    } catch (error) {
      console.error('Error in getUserById:', error);
      throw error;
    }
  },

  // ========== NEW OPTIMIZED LOOKUP ROUTES ==========
  
  /**
   * Get firms for dropdowns/selection - replaces getAllFirms
   */
  getFirmsLookup: async () => {
    try {
      const response = await apiClient.get('/api/v2/web/users/lookup/firms');
      return response.data;
    } catch (error) {
      console.error('Error in getFirmsLookup:', error);
      throw error;
    }
  },
  
  /**
   * Get cities with country data for dropdowns - replaces getAllCities + country lookups
   */
  getCitiesLookup: async () => {
    try {
      const response = await apiClient.get('/api/v2/web/users/lookup/cities');
      return response.data;
    } catch (error) {
      console.error('Error in getCitiesLookup:', error);
      throw error;
    }
  },
  
  /**
   * Get countries for dropdowns - replaces getAllCountries
   */
  getCountriesLookup: async () => {
    try {
      const response = await apiClient.get('/api/v2/web/users/lookup/countries');
      return response.data;
    } catch (error) {
      console.error('Error in getCountriesLookup:', error);
      throw error;
    }
  },

  /**
   * Get cities lookup data specifically for user management
   * Uses user-specific endpoint with stricter permissions
   */
  getCitiesLookupForUsers: async () => {
    try {
      const response = await apiClient.get('/api/v2/web/users/lookup/cities-for-users');
      return response.data;
    } catch (error) {
      console.error('Error in getCitiesLookupForUsers:', error);
      throw error;
    }
  },

  /**
   * Get users lookup data for dropdowns/selection
   * Available to users with appropriate permissions
   */
  getUsersLookup: async (firmId?: string) => {
    try {
      const url = firmId ? 
        `/api/v2/web/users/lookup/users?firm_id=${firmId}` : 
        '/api/v2/web/users/lookup/users';
      const response = await apiClient.get(url);
      return response.data;
    } catch (error) {
      console.error('Error in getUsersLookup:', error);
      throw error;
    }
  },

  // ========== EXISTING ROUTES (unchanged interface) ==========
  
  createUser: async (userData: any) => {
    try {
      const response = await apiClient.post('/api/v2/web/users', userData);
      return response.data;
    } catch (error) {
      console.error('Error in createUser:', error);
      throw error;
    }
  },
  
  updateUser: async (userId: string, userData: any) => {
    try {
      const response = await apiClient.put(`/api/v2/web/users/${userId}`, userData);
      return response.data;
    } catch (error) {
      console.error('Error in updateUser:', error);
      throw error;
    }
  },
  
  deleteUser: async (userId: string) => {
    try {
      const response = await apiClient.delete(`/api/v2/web/users/${userId}`);
      return response.status === 200 || response.status === 204;
    } catch (error) {
      console.error('Error in deleteUser:', error);
      throw error;
    }
  },

  // ========== ROLE MANAGEMENT ROUTES ==========
  
  getUserRoles: async (userId: string) => {
    try {
      const response = await apiClient.get(`/api/v2/web/users/${userId}/roles`);
      return response.data;
    } catch (error) {
      console.error('Error in getUserRoles:', error);
      throw error;
    }
  },
  
  updateUserRoles: async (userId: string, roleData: { roles: string[] }) => {
    try {
      const response = await apiClient.post(`/api/v2/web/users/${userId}/roles`, roleData);
      return response.data;
    } catch (error) {
      console.error('Error in updateUserRoles:', error);
      throw error;
    }
  },

  // ========== ADMIN-ONLY ROUTES ==========
  
  getUserRegistrations: async (userId: string) => {
    try {
      const response = await apiClient.get(`/api/v2/web/users/${userId}/registration`);
      return response.data;
    } catch (error) {
      console.error('Error in getUserRegistrations:', error);
      throw error;
    }
  },
  
  getUserVerification: async (userId: string) => {
    const response = await apiClient.get(`/api/v2/web/users/${userId}/verification`);
    return response.data;
  },

  // ========== PROFILE MANAGEMENT ROUTES ==========
  
  /**
   * Get current user's profile
   */
  getCurrentUserProfile: async () => {
    try {
      const response = await apiClient.get('/api/v2/web/users/me');
      return response.data;
    } catch (error) {
      console.error('Error in getCurrentUserProfile:', error);
      throw error;
    }
  },

  /**
   * Update current user's profile
   */
  updateCurrentUserProfile: async (profileData: any) => {
    try {
      const response = await apiClient.put('/api/v2/web/users/me', profileData);
      return response.data;
    } catch (error) {
      console.error('Error in updateCurrentUserProfile:', error);
      throw error;
    }
  }
};
